import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrackJS } from 'trackjs';
import { authentication } from "@microsoft/teams-js";
import { microsoftClientId, microsoftRequest } from '@shared/services/EnvironmentVariables';
import styled from "styled-components";
import { BodyRegular } from "../../atoms/fonts/Body";
import { OutlineButton } from "../../atoms/buttons/OutlineButton";
import { agent } from '@shared/services/agent';
import { isInTeams } from "@shared/utils/TeamsUtils";
import { useDispatch } from "react-redux";
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import TextField from "../../atoms/TextField";
import { updateCompanySetting } from '@features/settings/services/CompanyService';
import Colours from "../../atoms/Colours";
import { useTranslation } from "react-i18next";
import { ConfigurationSection } from "@features/settings/pages/StyleComponents";

export function ApplicationCalendarReadWritePermissionsChecker(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [tenantId, setTenantId] = useState('');
  const [editingTenantId, setEditingTenantId] = useState('');
  const [hasReadWriteCalendarPermission, setHasReadWriteCalendarPermission] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshPermissionCheck = useCallback(() => {
    setLoading(true);
    agent.requests.get('/permission-check').then((result: any) => {
      setHasReadWriteCalendarPermission(result?.hasRwCalendar ?? false);
      setTenantId(result?.tenantId ?? '');
      setEditingTenantId(result?.tenantId ?? '');
    }).catch((e: any) => {
      // On failure the panel shows "not granted", which may be wrong — track it.
      TrackJS?.track(`[PermissionsChecker] /permission-check failed: ${String(e?.message ?? e).slice(0, 200)}`);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refreshPermissionCheck();
  }, [refreshPermissionCheck])

  const url = useMemo(() => {
    // Derive the redirect from the current origin so admin consent returns to the environment the
    // user is actually on (prod/test/Teams), matching the rest of the Azure auth flow. Hardcoding
    // the prod URL previously sent consent back to prod from every environment.
    const redirectUri = `${window.location.origin}/login/azure`;
    return `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${microsoftClientId}&redirect_uri=${encodeURIComponent(redirectUri)}`
  }, [tenantId]);

  // Inside Teams the app runs in an iframe and the Microsoft consent page cannot be framed, so a
  // full-page redirect just blanks out. Drive consent through the Teams auth popup instead, reusing
  // the /login/admin-consent → /login/admin-consent-end (notifySuccess) flow.
  const requestConsentInTeams = async () => {
    const scopes = microsoftRequest.calendarScopes.join(' ');
    try {
      const result = await authentication.authenticate({
        url: `${window.location.origin}/login/admin-consent?scopes=${encodeURIComponent(scopes)}`,
        width: 600,
        height: 600,
        isExternal: true,
      });
      const parsed = JSON.parse(result || '{}') as { adminConsent?: boolean };
      if (parsed.adminConsent) {
        dispatch(successNotification('Permissions granted'));
        refreshPermissionCheck();
      } else {
        dispatch(failureNotification('Admin approval did not complete. Please try again.'));
      }
    } catch (e: any) {
      const msg = (e?.message ?? e?.toString?.() ?? '').toString();
      if (msg.includes('CancelledByUser')) return;
      TrackJS?.track(`[PermissionsChecker] teams admin consent failed: ${msg.slice(0, 200)}`);
      dispatch(failureNotification('Failed to start admin approval. Please try again.'));
    }
  };

  const onClick = () => {
    if (!tenantId) {
      dispatch(failureNotification('No Tenant ID associated with this account'));
      return;
    }
    if (isInTeams()) {
      requestConsentInTeams();
    } else {
      window.location.href = url;
    }
  }

  const updateTenantId = async () => {
    if (editingTenantId && editingTenantId !== tenantId) {
      try {
        await updateCompanySetting('TENANT_ID', editingTenantId);
        setTenantId(editingTenantId);
      } catch (e) {
        dispatch(failureNotification('Failed to save Tenant ID'))
      } finally {
        setLoading(false);
      }
    }
  }

  return <ConfigurationSection>
    <BodyRegular weight={600} style={{marginBottom: 8}} colour={Colours.blue}>{t('company-settings-page.tenant-id')}</BodyRegular>
    <TextField className="" disabled={loading}
               fullWidth={true}
               value={editingTenantId}
               onChange={setEditingTenantId}
               onBlur={updateTenantId} />
    <Container>
      <BodyRegular colour={Colours.blue} weight={600}>Application level permissions for Outlook</BodyRegular>
      <BodyRegular>For more comprehensive calendar integration you can give Team Today wider calendar access. This enables you to manage and view Outlook meeting room bookings via Team Today.</BodyRegular>
      <BodyRegular>You may need an IT Administrator to accept these permissions on behalf of your organization.</BodyRegular>
      <Wrapper>
        {!!tenantId ? <>
        {hasReadWriteCalendarPermission && <BodyRegular weight={600}>Permissions have been accepted</BodyRegular>}
        </> : <>
          <BodyRegular weight={600} colour={Colours.alertRed}>Your Azure Tenant ID needs to be set</BodyRegular>
        </>}
        <OutlineButton click={onClick} text={'Accept permissions'} disabled={!tenantId || hasReadWriteCalendarPermission} />
      </Wrapper>
    </Container>
  </ConfigurationSection>
}

interface Props {
}

const Wrapper = styled.div`
  display: flex;
  justify-self: flex-end;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  button {
    margin: 0;
    margin-left: auto;
    width: 180px;
  }
`

const Container = styled.div`
`
